import { useState } from "react";
import { RichTextEditor, useRichTextEditorContext } from "@mantine/tiptap";
import { Popover, TextInput, Button, Group, Stack } from "@mantine/core";
import { IconRectangle } from "@tabler/icons-react";
import { buildEmailButton } from "@congrs/utils";
import { DEFAULT_EVENT_ACCENT_COLOR } from "@congrs/schemas";

const URL_PATTERN = /^https?:\/\/.+$/i;
// Substituted per recipient at send time so each invitee gets their own
// tokenized register link. Accepted in the URL field as a valid target.
const REGISTER_LINK_PLACEHOLDER = "{{registerLink}}";

/**
 * Read the text content of the currently selected link mark range. Used to
 * pre-fill the Label field when editing an existing button.
 */
const getActiveLinkText = (editor) => {
  const { state } = editor;
  const { $from } = state.selection;
  const mark = state.schema.marks.link;
  if (!mark) return "";

  let start = $from.pos;
  let end = $from.pos;
  $from.parent.descendants((node, pos) => {
    if (node.marks.some((m) => m.type === mark)) {
      const nodeStart = $from.start() + pos;
      const nodeEnd = nodeStart + node.nodeSize;
      if (nodeStart <= $from.pos && $from.pos <= nodeEnd) {
        start = nodeStart;
        end = nodeEnd;
        return false;
      }
    }
    return true;
  });
  return state.doc.textBetween(start, end);
};

/**
 * Toolbar control that inserts (or edits) a styled email button. The button
 * is plain HTML built by the shared @congrs/utils helper — no custom Tiptap
 * node — so the editor, the preview, and the sent email render the exact
 * same markup.
 */
const ButtonControl = ({ accentColor, registerLinkPreset = false }) => {
  const { editor } = useRichTextEditorContext();
  const [opened, setOpened] = useState(false);
  const [label, setLabel] = useState("");
  const [url, setUrl] = useState("");
  const [touched, setTouched] = useState(false);

  const trimmedUrl = url.trim();
  const isRegisterLink = trimmedUrl === REGISTER_LINK_PLACEHOLDER;
  const isUrlValid = URL_PATTERN.test(trimmedUrl) || isRegisterLink;
  const showUrlError = touched && !isUrlValid;

  const linkAttrs = editor?.getAttributes("link") || {};
  const isEditing = linkAttrs["data-email-button"] === "true";

  const open = () => {
    if (isEditing) {
      setLabel(getActiveLinkText(editor) || "");
      setUrl(linkAttrs.href && linkAttrs.href !== "#" ? linkAttrs.href : "");
    } else {
      setLabel("");
      setUrl("");
    }
    setTouched(false);
    setOpened(true);
  };

  const handleSubmit = () => {
    if (!editor) return;
    if (!isUrlValid) {
      setTouched(true);
      return;
    }
    const html = buildEmailButton({
      href: trimmedUrl,
      label: label || "Button",
      accentColor: accentColor || DEFAULT_EVENT_ACCENT_COLOR,
    });

    if (isEditing) {
      // Replace the existing button: extend selection to cover the wrapping
      // paragraph, then insert the fresh HTML in its place.
      editor
        .chain()
        .focus()
        .selectParentNode()
        .deleteSelection()
        .insertContent(html)
        .run();
    } else {
      // Insert the button followed by an empty unaligned paragraph so the
      // cursor lands somewhere that won't inherit the centered alignment.
      editor.chain().focus().insertContent(`${html}<p></p>`).run();
    }

    setOpened(false);
  };

  const handleRemove = () => {
    if (!editor) return;
    editor.chain().focus().selectParentNode().deleteSelection().run();
    setOpened(false);
  };

  // Prefill the form with the event register link and a default label, leaving
  // the popover open so the organizer can rename the button (e.g. "Apply",
  // "Prijavi se") before inserting it.
  const applyRegisterPreset = () => {
    setLabel("Register");
    setUrl(REGISTER_LINK_PLACEHOLDER);
    setTouched(false);
  };

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom"
      withArrow
      shadow="md"
      width={260}
      trapFocus
    >
      <Popover.Target>
        <RichTextEditor.Control
          onClick={open}
          active={isEditing}
          aria-label="Insert button"
          title="Insert button"
        >
          <IconRectangle size={16} stroke={1.5} />
        </RichTextEditor.Control>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap="xs">
          {registerLinkPreset && !isEditing && (
            <Button size="xs" variant="light" onClick={applyRegisterPreset}>
              Use event register link
            </Button>
          )}
          <TextInput
            label="Label"
            placeholder="e.g. Register now"
            value={label}
            onChange={(e) => setLabel(e.currentTarget.value)}
            data-autofocus
          />
          <TextInput
            label="URL"
            placeholder="https://example.com"
            description={
              isRegisterLink
                ? "Personalized register link, filled in per recipient at send time"
                : undefined
            }
            value={url}
            onChange={(e) => setUrl(e.currentTarget.value)}
            onBlur={() => setTouched(true)}
            error={showUrlError && "URL must start with http:// or https://"}
          />
          <Group gap="xs" grow>
            <Button size="xs" onClick={handleSubmit} disabled={!isUrlValid}>
              {isEditing ? "Update" : "Insert button"}
            </Button>
            {isEditing && (
              <Button
                size="xs"
                variant="light"
                color="red"
                onClick={handleRemove}
              >
                Remove
              </Button>
            )}
          </Group>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
};

export default ButtonControl;
